import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Container,
    Box,
    Avatar,
    Typography,
    TextField,
    Button,
    Link,
    Alert,
    Paper,
    useTheme,
    useMediaQuery
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        try {
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            const error = err as { response?: { data?: { message?: string } } };
            setError(error.response?.data?.message || 'Failed to login');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: isMobile ? 4 : 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: isMobile ? 'calc(100vh - 32px)' : 'auto',
                    justifyContent: isMobile ? 'center' : 'flex-start',
                    px: isMobile ? 2 : 0,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: isMobile ? 3 : 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: isMobile ? '100%' : 400,
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 48, height: 48 }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant={isMobile ? "h5" : "h4"} sx={{ mb: 2 }}>
                        Sign in to WrkFlowPro
                    </Typography>
                    
                    {error && (
                        <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus={!isMobile}
                            value={email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            disabled={isLoading}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '16px' : '14px',
                                }
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            disabled={isLoading}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: isMobile ? '16px' : '14px',
                                }
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ 
                                mt: 3, 
                                mb: 2,
                                minHeight: isMobile ? '48px' : '44px',
                                fontSize: isMobile ? '16px' : '14px'
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Signing in...' : 'Sign In'}
                        </Button>
                        <Box sx={{ textAlign: 'center' }}>
                            <Link 
                                component={RouterLink} 
                                to="/register" 
                                variant="body2"
                                sx={{
                                    fontSize: isMobile ? '16px' : '14px',
                                    minHeight: isMobile ? '44px' : 'auto',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Don&apos;t have an account? Sign Up
                            </Link>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default Login;
